/**
 * PublicHeader Component
 * Navigation header for public pages (non-authenticated users)
 * Optimized for mobile with hamburger menu
 */

import { skipToken } from "@reduxjs/toolkit/query";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { tokenStorage } from "../../config/api";
import { useGetProfileQuery, useLogoutMutation } from "../../store/api/authApi";
import Icon from "../AppIcon";
import Button from "../ui/Button";

interface PublicHeaderProps {
	/**
	 * Optional callback to scroll to a section on the page
	 */
	onScrollToProperties?: () => void;
}

const PublicHeader: React.FC<PublicHeaderProps> = ({
	onScrollToProperties,
}) => {
	const navigate = useNavigate();
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

	// Check if user is logged in
	const accessToken = tokenStorage.getAccessToken();
	const { data: currentUser } = useGetProfileQuery(
		accessToken ? undefined : skipToken,
	);
	const [logout] = useLogoutMutation();

	// Get dashboard route based on user role
	const getDashboardRoute = () => {
		if (!currentUser) return "/";
		switch (currentUser.role) {
			case "admin":
				return "/admin-dashboard";
			case "tenant":
				return "/tenant-dashboard";
			case "technician":
				return "/technician-dashboard";
			case "agent":
				return "/agent-dashboard";
			default:
				return "/";
		}
	};

	const handleLogout = async () => {
		try {
			await logout().unwrap();
			tokenStorage.clearTokens();
			navigate("/login");
		} catch (error) {
			console.error("Logout failed:", error);
			// Clear tokens anyway and redirect
			tokenStorage.clearTokens();
			navigate("/login");
		}
	};

	// Close mobile menu when clicking outside
	useEffect(() => {
		if (!isMobileMenuOpen) return;

		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			// Check if click is outside both menu and button
			if (
				!target.closest(".mobile-menu") &&
				!target.closest(".mobile-menu-button")
			) {
				setIsMobileMenuOpen(false);
			}
		};

		// Use setTimeout to avoid immediate closure on button click
		const timeoutId = setTimeout(() => {
			document.addEventListener("click", handleClickOutside, true);
		}, 100);

		return () => {
			clearTimeout(timeoutId);
			document.removeEventListener("click", handleClickOutside, true);
		};
	}, [isMobileMenuOpen]);

	const handleScrollToProperties = () => {
		setIsMobileMenuOpen(false);
		// Navigate to properties page instead of scrolling
		navigate("/properties");
	};

	const handleNavClick = () => {
		setIsMobileMenuOpen(false);
	};

	return (
		<header className="bg-card border-b border-border sticky top-0 z-[100] backdrop-blur-sm bg-card/95 relative">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex items-center justify-between h-16">
					{/* Logo / Brand */}
					<Link
						to="/"
						className="flex items-center space-x-2 smooth-transition hover:opacity-80"
						onClick={() => setIsMobileMenuOpen(false)}
					>
						<img
							src="/assets/images/desktop_logo.png"
							alt="Dofarahub Logo"
							className="h-8 w-auto object-contain"
						/>
					</Link>

					{/* Desktop Navigation Links - Optimized for tablet */}
					<nav className="hidden md:flex items-center space-x-3 lg:space-x-6">
						<button
							onClick={handleScrollToProperties}
							className="text-xs md:text-sm font-medium text-muted-foreground hover:text-foreground smooth-transition px-2 py-1 rounded-md hover:bg-muted/50"
						>
							Browse Properties
						</button>
					</nav>

					{/* Desktop Auth Buttons - Optimized for tablet */}
					<div className="hidden md:flex items-center space-x-2 lg:space-x-3">
						{currentUser ? (
							<>
								<Button
									onClick={() => navigate(getDashboardRoute())}
									className="text-xs md:text-sm px-3 lg:px-4 h-8 lg:h-9"
								>
									<Icon
										name="Home"
										size={14}
										className="mr-1.5 lg:mr-2 lg:w-4 lg:h-4"
									/>
									<span className="hidden lg:inline">Go to Dashboard</span>
									<span className="lg:hidden">Dashboard</span>
								</Button>
								<Button
									variant="outline"
									onClick={handleLogout}
									className="text-xs md:text-sm px-3 lg:px-4 h-8 lg:h-9"
								>
									<Icon
										name="LogOut"
										size={14}
										className="mr-1.5 lg:mr-2 lg:w-4 lg:h-4"
									/>
									<span className="hidden lg:inline">Logout</span>
									<span className="lg:hidden">Out</span>
								</Button>
							</>
						) : (
							<>
								<Button
									variant="ghost"
									onClick={() => navigate("/login")}
									className="text-xs md:text-sm px-3 lg:px-4 h-8 lg:h-9"
								>
									Sign In
								</Button>
								<Button
									onClick={() => navigate("/register")}
									className="text-xs md:text-sm px-3 lg:px-4 h-8 lg:h-9"
								>
									<span className="">Sign Up</span>
								</Button>
							</>
						)}
					</div>

					{/* Mobile Menu Button */}
					<button
						className="mobile-menu-button md:hidden p-2 rounded-lg hover:bg-muted smooth-transition"
						onClick={(e) => {
							e.stopPropagation();
							setIsMobileMenuOpen((prev) => !prev);
						}}
						aria-label="Toggle mobile menu"
						aria-expanded={isMobileMenuOpen}
						type="button"
					>
						{isMobileMenuOpen ? (
							<Icon name="X" size={24} className="text-foreground" />
						) : (
							<Icon name="Menu" size={24} className="text-foreground" />
						)}
					</button>
				</div>
			</div>

			{/* Mobile Menu Dropdown */}
			{isMobileMenuOpen && (
				<div className="mobile-menu absolute top-full left-0 right-0 bg-background border-b border-border shadow-lg z-50 md:hidden">
					<div className="container mx-auto px-4 py-4 space-y-3">
						{/* Mobile Navigation Links */}
						<nav className="flex flex-col space-y-2">
							<button
								onClick={handleScrollToProperties}
								className="text-center text-base font-medium text-foreground py-3 px-4 rounded-lg hover:bg-muted smooth-transition"
							>
								Browse Properties
							</button>
						</nav>

						{/* Divider */}
						<div className="border-t border-border my-3" />

						{/* Mobile Auth Buttons */}
						<div className="flex flex-col space-y-2">
							{currentUser ? (
								<>
									<Button
										onClick={() => {
											setIsMobileMenuOpen(false);
											navigate(getDashboardRoute());
										}}
										className="w-full justify-center text-base py-3"
									>
										<Icon name="Home" size={18} className="mr-2" />
										Go to Dashboard
									</Button>
									<Button
										variant="outline"
										onClick={() => {
											setIsMobileMenuOpen(false);
											handleLogout();
										}}
										className="w-full justify-center text-base py-3"
									>
										<Icon name="LogOut" size={18} className="mr-2" />
										Logout
									</Button>
								</>
							) : (
								<>
									<Button
										variant="ghost"
										onClick={() => {
											setIsMobileMenuOpen(false);
											navigate("/login");
										}}
										className="w-full justify-center text-base py-3"
									>
										Sign In
									</Button>
									<Button
										onClick={() => {
											setIsMobileMenuOpen(false);
											navigate("/register");
										}}
										className="w-full justify-center text-base py-3"
									>
										Sign Up
									</Button>
								</>
							)}
						</div>
					</div>
				</div>
			)}
		</header>
	);
};

export default PublicHeader;
